import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Snowflake, School, Save } from "lucide-react";
import { TIPO_BADGE, ESTADO_BADGE, ExpiryChip } from "./TasasPanel";

// V1.51 · Bloque visual de tasas del alumno (ficha › Exámenes DGT).
// Muestra nº, estado, congeladas, fórmula restante, caducidad y los
// consumos de la anterior autoescuela (traslados), editables inline.
export const StudentTasasBlock = ({ student }) => {
  const [tasas, setTasas] = useState([]);
  const [prevEdit, setPrevEdit] = useState(null); // {id, suspensos, teorico, note}

  const load = useCallback(async () => {
    if (!student?.id) return;
    try {
      const { data } = await api.get(`/students/${student.id}/dgt-tasas`);
      setTasas(data || []);
    } catch (_) { /* noop */ }
  }, [student?.id]);
  useEffect(() => { load(); }, [load]);

  if (tasas.length === 0) return null;

  const savePrev = async (id) => {
    try {
      await api.patch(`/dgt-tasas/${id}`, {
        prev_school_suspensos: Number(prevEdit.suspensos) || 0,
        prev_school_teorico_apto: !!prevEdit.teorico,
        prev_school_note: prevEdit.note || "",
      });
      toast.success("Consumos de la anterior autoescuela actualizados");
      setPrevEdit(null);
      await load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="student-tasas-block">
      <h4 className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold flex items-center gap-1.5">
        <Ticket className="h-3.5 w-3.5 text-blue-600" /> Tasas DGT del alumno ({tasas.length})
      </h4>
      {tasas.map((t) => {
        const c = t.consumption || {};
        const editing = prevEdit?.id === t.id;
        return (
          <div key={t.id} data-testid={`student-tasa-${t.id}`}
            className={`rounded-xl border-2 p-4 ${c.estado === "agotada" ? "border-rose-300 bg-rose-50/40" : c.estado === "completada" ? "border-emerald-300 bg-emerald-50/40" : "border-blue-200 bg-blue-50/30"}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-black text-base text-neutral-900">Nº {t.number}</span>
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${TIPO_BADGE[t.tipo]}`}>
                {t.tipo} · {t.tipo_label} ({t.code})
              </span>
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${ESTADO_BADGE[c.estado] || ""}`}>
                {c.estado}
              </span>
              {c.congeladas > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 border border-sky-300 px-2 py-0.5 text-[10px] font-bold">
                  <Snowflake className="h-3 w-3" /> congelada {c.congeladas}
                </span>
              )}
              <span className="ml-auto flex items-center gap-1.5 text-[10px] text-neutral-500">
                compra {t.purchase_date} · <ExpiryChip dias={c.dias_caducidad} />
              </span>
            </div>
            <p className="mt-2 text-sm font-bold text-neutral-800" data-testid={`tasa-formula-${t.id}`}>{c.formula}</p>
            {(c.detalle || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {c.detalle.map((d, i) => (
                  <span key={i} className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${d.result === "APTO" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : d.result.startsWith("CONGELADA") ? "bg-sky-50 border-sky-300 text-sky-800" : "bg-rose-50 border-rose-300 text-rose-800"}`}>
                    {d.date} · {d.kind}: {d.result}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-neutral-200/60">
              {editing ? (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <School className="h-3.5 w-3.5 text-violet-600" />
                  <label className="font-semibold text-neutral-600">Suspensos anterior autoescuela:</label>
                  <input type="number" min="0" max="2" value={prevEdit.suspensos}
                    onChange={(e) => setPrevEdit({ ...prevEdit, suspensos: e.target.value })}
                    data-testid={`tasa-prev-susp-${t.id}`}
                    className="w-14 rounded-md border border-neutral-300 px-1.5 h-7 text-center font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
                  <label className="inline-flex items-center gap-1 font-semibold text-neutral-600">
                    <input type="checkbox" checked={prevEdit.teorico}
                      onChange={(e) => setPrevEdit({ ...prevEdit, teorico: e.target.checked })}
                      data-testid={`tasa-prev-teorico-${t.id}`} className="accent-violet-600" />
                    Teórico aprobado allí
                  </label>
                  <input value={prevEdit.note} placeholder="Nota (autoescuela de origen...)"
                    onChange={(e) => setPrevEdit({ ...prevEdit, note: e.target.value })}
                    className="flex-1 min-w-[140px] rounded-md border border-neutral-300 px-1.5 h-7 focus:outline-none focus:ring-2 focus:ring-violet-500" />
                  <button type="button" onClick={() => savePrev(t.id)} data-testid={`tasa-prev-save-${t.id}`}
                    className="inline-flex items-center gap-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white px-2 h-7 text-[11px] font-bold">
                    <Save className="h-3 w-3" /> Guardar
                  </button>
                  <button type="button" onClick={() => setPrevEdit(null)} className="text-[11px] text-neutral-500 hover:underline">Cancelar</button>
                </div>
              ) : (
                <button type="button"
                  onClick={() => setPrevEdit({ id: t.id, suspensos: c.prev_school_suspensos || 0, teorico: !!c.prev_school_teorico_apto, note: t.prev_school_note || "" })}
                  data-testid={`tasa-prev-toggle-${t.id}`}
                  className="text-[11px] text-violet-700 font-semibold hover:underline inline-flex items-center gap-1">
                  <School className="h-3 w-3" />
                  {c.prev_school_suspensos > 0 || c.prev_school_teorico_apto
                    ? `Anterior autoescuela: ${c.prev_school_suspensos} suspenso(s) · teórico ${c.prev_school_teorico_apto ? "APROBADO" : "no aprobado"}${t.prev_school_note ? ` · ${t.prev_school_note}` : ""} (editar)`
                    : "Traslado: registrar consumos de la anterior autoescuela"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StudentTasasBlock;
